/**
 * Standalone inland marine risk card (#33164 — docs/farm-standalone-inland-marine.md).
 *
 * A building-less farm risk: it insures scheduled personal property and nothing else, so its SPP
 * `coverages.scheduleCoverages` items ARE the risk. That drives three deliberate differences from the
 * dwelling/FO-6 cards next to it:
 *
 * - **No edit pencil.** The other cards open EditRiskModal to change building/property details; this
 *   risk has no building to edit, so the affordance would open an empty modal. FO-6
 *   scheduled/unscheduled set the same precedent.
 * - **One green band, no beige property band.** The dwelling cards render a beige `riskCard` band of
 *   property fields above a green `coverageCard` band of coverages. Here there are no property fields
 *   and no coverage fields, so the card is a single green band whose only content is the item list.
 * - **The item form renders bare** (`hideCollapser`). It is the only section on the card, so the
 *   collapser heading and chevron it normally carries as one sub-risk among many would be redundant
 *   chrome wrapping the whole card body.
 *
 * Registered in the `riskTypes` map, so it renders inline in the location's risk list like the
 * dwelling cards rather than in a sibling section the way FO-6 scheduled/unscheduled do. Keeping the
 * standard `id={fieldName}` (the `farmLocations[N].risks[M]` path) is what lets the farm navigator's
 * "Go" scroll and open-risk tracking work without a special case — the FO-6 cards need one only
 * because they use non-standard ids.
 */
import React from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import { FarmRiskType, INLAND_MARINE_RISK_NAME } from '@ourbranch/lookups';

import CollapsibleCard from 'core/components/collapsible-card';
import { Label } from 'core/components/label';
import { useActiveFormState } from 'common/hooks/use-active-form-state';
import { useFarmOpenSections } from 'common/hooks/farm-open-sections-context';

import { InlandMarineForm } from '../../sub-risks';
import useStyles from '../../risk.styles';

const riskType = FarmRiskType.InlandMarine;

const InlandMarineRisk = ({ item, index, fieldName, removeRisk, canRemoveRisk, locationAddress }) => {
  const classes = useStyles();
  const { disableForm: disableRiskForm } = useActiveFormState(fieldName);
  const { isCurrentSectionOpen, toggleSection } = useFarmOpenSections(fieldName);

  return (
    <CollapsibleCard
      content={
        <div className={classes.header}>
          <div className={classes.headerRow}>
            <Label type="infoTitleSmall">{INLAND_MARINE_RISK_NAME}</Label>
          </div>
        </div>
      }
      isRemovable={canRemoveRisk}
      onDelete={() => removeRisk(index)}
      disableRemoveButton={disableRiskForm}
      open={isCurrentSectionOpen}
      onChange={toggleSection}
      id={fieldName}
    >
      <Grid container spacing={2} className={classes.coverageCard}>
        <Grid item xs={12}>
          <InlandMarineForm
            fieldName={`${fieldName}.coverages.scheduleCoverages`}
            disabled={disableRiskForm}
            riskType={riskType}
            coverages={item?.coverages || {}}
            // Pass the state explicitly: InlandMarineForm otherwise reads location 0's address, which
            // is the wrong state for a risk on any later location.
            state={locationAddress?.state}
            hideCollapser
          />
        </Grid>
      </Grid>
    </CollapsibleCard>
  );
};

InlandMarineRisk.propTypes = {
  item: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  fieldName: PropTypes.string.isRequired,
  removeRisk: PropTypes.func.isRequired,
  canRemoveRisk: PropTypes.bool,
  locationAddress: PropTypes.object
};

InlandMarineRisk.defaultProps = {
  canRemoveRisk: true,
  locationAddress: undefined
};

export default InlandMarineRisk;
